import { useState } from "react";
import { ChevronDown, ShieldCheck } from "lucide-react";
import { clsx } from "clsx";
import type { GrantedPermission } from "../api/settings";
import { useMyPermissions } from "../hooks/useSettings";

/**
 * „Moje uprawnienia" — rozwijana lista uprawnień z dodatkowych flag konta (Panel CEO →
 * Role i uprawnienia), które użytkownik FAKTYCZNIE ma. Backend liczy je tymi samymi
 * bramkami co moduły, więc lista obejmuje też to, co ktoś ma z pełnionej funkcji
 * (CEO, dyrektor techniczny…), choć flagi nikt mu nie zapalał.
 *
 * Lista przychodzi dopiero po rozwinięciu — Ustawienia nie pytają API o dane, których
 * nie wyświetlają (zob. SettingsPage.test).
 */
export function MyPermissionsSection() {
  const [open, setOpen] = useState(false);
  const { data: permissions = [], isSuccess, isLoading } = useMyPermissions(open);

  const groups = permissions.reduce<Map<string, GrantedPermission[]>>((acc, p) => {
    acc.set(p.group, [...(acc.get(p.group) ?? []), p]);
    return acc;
  }, new Map());

  return (
    <div className="bg-white dark:bg-gray-800 p-6">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 text-left"
      >
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
            <ShieldCheck size={14} className="text-gray-400 dark:text-gray-500" />
            Moje uprawnienia
            {isSuccess && (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                {permissions.length}
              </span>
            )}
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Uprawnienia nadawane dodatkowo — także te, które masz z racji pełnionej funkcji.
          </p>
        </div>
        <ChevronDown
          size={16}
          className={clsx(
            "flex-shrink-0 text-gray-400 transition-transform",
            open && "rotate-180",
          )}
        />
      </button>

      {open && (
        <div className="mt-4">
          {isLoading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Wczytywanie…</p>
          ) : permissions.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Nie masz uprawnień nadawanych dodatkowo.
            </p>
          ) : (
            <div className="space-y-4">
              {[...groups.entries()].map(([group, rows]) => (
                <div key={group}>
                  <p className="mb-1.5 font-mono text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                    {group}
                  </p>
                  <ul className="space-y-2">
                    {rows.map((p) => (
                      <li key={p.key}>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {p.label}
                          </span>
                          <span
                            title={
                              p.source === "auto"
                                ? "Masz to uprawnienie z racji pełnionej funkcji."
                                : "Uprawnienie nadane Twojemu kontu."
                            }
                            className={clsx(
                              "flex-shrink-0 rounded px-1.5 py-0.5 text-[11px]",
                              p.source === "auto"
                                ? "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                                : "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
                            )}
                          >
                            {p.source === "auto" ? "z funkcji" : "nadane"}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {p.description}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
